import { useEffect, useState } from "react";
import { Button, Popconfirm, Select, Spin, Table, message } from "antd";
import {
  getClientAccountOwnersByCountry,
  getAcountOwnerList,
  unassignCustomerCountryAccountOwner,
} from "../../services/ApiService";
import CountryAccountOwnerChangeModal from "../ClientCustomer/CountryAccountOwnerChangeModal";
import { hasPermission } from "../GlobalFunction";

const { Option } = Select;

// Client Update Form's "Account Owners by Country" -- confirmed with the
// user, 2026-10-26: replaces the Client List's removed "Change Account
// Owner" column. One row per (customer, country) pair across every
// customer under this client, since account ownership is tracked per
// customer+country, not per client+country (that's why this is a
// SEPARATE table from Country-wise Fees above, which is client+country
// grained). Changing a row's owner reuses the exact same customer+country
// reassignment flow already built for the Customer List page
// (CountryAccountOwnerChangeModal) -- picking a new owner here only ever
// affects that one customer's employees in that one country, never the
// whole client at once.
const AccountOwnersByCountry = ({ clientId }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [accountOwnerOptions, setAccountOwnerOptions] = useState([]);
  const [unassigning, setUnassigning] = useState(null); // row key currently being unassigned, or null
  const [changeModal, setChangeModal] = useState(null); // {customerId, customerName, countryId, countryName, newOwnerId, newOwnerLabel}

  const canEdit = hasPermission("client_list", "update");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await getClientAccountOwnersByCountry(clientId);
      if (res?.responseCode === 200) {
        setRows(res.rows || []);
      } else {
        message.error(res?.responseMessage || "Could not load account owners.");
      }
    } catch (error) {
      console.error("Error fetching client account owners by country:", error);
      message.error("Could not load account owners.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientId) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    const fetchAccountOwners = async () => {
      try {
        const res = await getAcountOwnerList();
        if (res?.responseCode === 200) {
          setAccountOwnerOptions(
            (res?.res || []).map((owner) => ({ label: owner.name, value: owner.id }))
          );
        }
      } catch (error) {
        console.error("Error fetching account owners:", error);
      }
    };
    fetchAccountOwners();
  }, []);

  const ownerLabel = (ownerId) =>
    accountOwnerOptions.find((o) => o.value === ownerId)?.label || ownerId || "Unassigned";

  const handleUnassign = async (row) => {
    const rowKey = `${row.client_customer_id}-${row.country_id}`;
    setUnassigning(rowKey);
    try {
      const res = await unassignCustomerCountryAccountOwner(row.client_customer_id, row.country_id);
      if (res?.responseCode === 200) {
        message.success("Account owner unassigned");
        fetchRows();
      } else {
        message.error(res?.responseMessage || "Failed to unassign account owner.");
      }
    } catch (error) {
      console.error("Error unassigning country account owner:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setUnassigning(null);
    }
  };

  const columns = [
    { title: "Customer", dataIndex: "client_customer_name", key: "client_customer_name" },
    { title: "Country", dataIndex: "country_name", key: "country_name" },
    {
      title: "Account Owner",
      key: "owner",
      render: (_, row) =>
        canEdit ? (
          <Select
            style={{ width: 200 }}
            placeholder="Unassigned"
            value={row.current_account_owner || undefined}
            onChange={(value) =>
              value &&
              setChangeModal({
                customerId: row.client_customer_id,
                customerName: row.client_customer_name,
                countryId: row.country_id,
                countryName: row.country_name,
                newOwnerId: value,
                newOwnerLabel: ownerLabel(value),
              })
            }
            showSearch
            optionFilterProp="children"
          >
            {accountOwnerOptions.map((o) => (
              <Option key={o.value} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>
        ) : (
          row.current_account_owner_name || "Unassigned"
        ),
    },
  ];

  if (canEdit) {
    columns.push({
      title: "",
      key: "unassign",
      render: (_, row) =>
        row.current_account_owner ? (
          <Popconfirm
            title={`Unassign the owner for ${row.client_customer_name} / ${row.country_name}?`}
            onConfirm={() => handleUnassign(row)}
            okText="Unassign"
            cancelText="Cancel"
          >
            <Button
              size="small"
              danger
              loading={unassigning === `${row.client_customer_id}-${row.country_id}`}
            >
              Unassign
            </Button>
          </Popconfirm>
        ) : null,
    });
  }

  return (
    <div className="Reviewsection">
      <h3 className="section-title">Account Owners by Country</h3>
      <p style={{ color: "#888", fontSize: "0.85rem", marginTop: 0 }}>
        One row per customer and the countries its employees are in under this client. Changing a
        row's owner moves only that customer's employees in that country -- not the whole client.
      </p>

      <Spin spinning={loading}>
        <Table
          rowKey={(row) => `${row.client_customer_id}-${row.country_id}`}
          dataSource={rows}
          columns={columns}
          pagination={false}
          locale={{ emptyText: "No customers with employees found under this client yet." }}
          size="small"
        />
      </Spin>

      <CountryAccountOwnerChangeModal
        open={!!changeModal}
        customerId={changeModal?.customerId}
        customerName={changeModal?.customerName}
        countryId={changeModal?.countryId}
        countryName={changeModal?.countryName}
        newOwnerId={changeModal?.newOwnerId}
        newOwnerLabel={changeModal?.newOwnerLabel}
        onClose={() => setChangeModal(null)}
        onSaved={() => fetchRows()}
      />
    </div>
  );
};

export default AccountOwnersByCountry;
